import React, { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";

const RichTextEditor = ({ value, onChange, limit = 600 }) => {
  const isUpdatingFromParent = useRef(false);
  const [charCount, setCharCount] = useState(0);

  const getDocText = (doc) => {
    return (doc.textContent || "").replace(/\u00a0/g, " ");
  };

  const getHTMLTextLength = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return (div.textContent || "").replace(/\u00a0/g, " ").length;
  };

  const trimEditorToLimit = (editor) => {
    let count = 0;
    let cutPos = null;

    editor.state.doc.descendants((node, pos) => {
      if (cutPos !== null) return false;

      if (node.isText) {
        const text = node.text || "";
        const nextCount = count + text.length;

        if (nextCount > limit) {
          cutPos = pos + (limit - count);
          return false;
        }

        count = nextCount;
      }

      return true;
    });

    if (cutPos !== null) {
      editor.commands.deleteRange({
        from: cutPos,
        to: editor.state.doc.content.size,
      });
    }

    const finalLen = Math.min(editor.state.doc.textContent.length, limit);
    setCharCount(finalLen);
    onChange(editor.getHTML(), finalLen);
  };

  const truncateHTML = (html, max) => {
    const div = document.createElement("div");
    div.innerHTML = html;

    let count = 0;

    const walk = (node) => {
      if (count >= max) {
        node.remove();
        return;
      }

      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || "";
        const allowed = max - count;

        if (text.length > allowed) {
          node.textContent = text.slice(0, allowed);
          count = max;
        } else {
          count += text.length;
        }

        return;
      }

      Array.from(node.childNodes).forEach(walk);
    };

    Array.from(div.childNodes).forEach(walk);
    return div.innerHTML;
  };

  const editor = useEditor({
    extensions: [StarterKit],
    content: value || "",

    editorProps: {
      attributes: {
        class: "custom-rich-editor",
      },

      handleTextInput(view, from, to, text) {
        const currentLen = getDocText(view.state.doc).length;
        const selectedLen = view.state.doc.textBetween(
          from,
          to,
          "\n",
          "\n",
        ).length;
        const available = limit - (currentLen - selectedLen);

        if (available <= 0) return true;

        if (text.length > available) {
          view.dispatch(
            view.state.tr.insertText(text.slice(0, available), from, to),
          );
          return true;
        }

        return false;
      },

      handlePaste(view, event) {
        event.preventDefault();

        const html = event.clipboardData?.getData("text/html") || "";
        const text = event.clipboardData?.getData("text/plain") || "";

        const { from, to } = view.state.selection;

        const currentLen = getDocText(view.state.doc).length;
        const selectedLen = view.state.doc.textBetween(
          from,
          to,
          "\n",
          "\n",
        ).length;
        const available = limit - (currentLen - selectedLen);
        const cleanPasteHTML = (html) => {
          const div = document.createElement("div");
          div.innerHTML = html;

          while (
            div.firstChild &&
            ((div.firstChild.nodeType === Node.TEXT_NODE &&
              !div.firstChild.textContent.replace(/\u00a0/g, " ").trim()) ||
              (div.firstChild.nodeType === Node.ELEMENT_NODE &&
                !div.firstChild.textContent.replace(/\u00a0/g, " ").trim() &&
                !div.firstChild.querySelector("img, video, iframe")))
          ) {
            div.firstChild.remove();
          }

          return div.innerHTML;
        };
        if (available <= 0) return true;

        if (html && html.trim()) {
          const cleanedHTML = cleanPasteHTML(html);

          editor
            .chain()
            .focus()
            .insertContentAt({ from, to }, cleanedHTML, {
              parseOptions: { preserveWhitespace: "full" },
            })
            .run();

          setTimeout(() => {
            trimEditorToLimit(editor);
          }, 0);

          return true;
        }

        view.dispatch(
          view.state.tr.insertText(text.slice(0, available), from, to),
        );
        return true;
      },
    },

    onUpdate({ editor }) {
      if (isUpdatingFromParent.current) return;

      const len = getDocText(editor.state.doc).length;
      setCharCount(len);
      onChange(editor.getHTML(), len);
    },
  });

  useEffect(() => {
    if (!editor) return;

    const newHTML = value || "";

    if (editor.getHTML() !== newHTML) {
      isUpdatingFromParent.current = true;
      editor.commands.setContent(newHTML, false);

      const len = getDocText(editor.state.doc).length;
      setCharCount(len);

      isUpdatingFromParent.current = false;
    }
  }, [value, editor]);

  const remaining = Math.max(0, limit - charCount);

  return (
    <>
      <style>{`
      .custom-rich-editor > *:first-child,
.ProseMirror > *:first-child {
  margin-top: 0 !important;
}
        .custom-rich-editor,
        .ProseMirror {
          width: 100%;
          max-width: 100%;
          min-height: 180px;
          outline: none;
          box-sizing: border-box;
          white-space: pre-wrap;
          overflow-wrap: break-word;
          word-break: break-word;
        }

        .custom-rich-editor {
          border: 1px solid #ddd;
          border-radius: 8px;
          padding: 12px;
          font-size: 15px;
          line-height: 1.7;
          background: #fff;
          cursor: text;
        }

        .custom-rich-editor p {
          margin: 6px 0;
        }

        .custom-rich-editor ul {
          list-style-type: disc !important;
          padding-left: 28px !important;
          margin: 10px 0 !important;
        }

        .custom-rich-editor ol {
          list-style-type: decimal !important;
          padding-left: 28px !important;
          margin: 10px 0 !important;
        }

        .custom-rich-editor li {
          display: list-item !important;
          margin: 4px 0;
        }

        .custom-rich-editor strong,
        .custom-rich-editor b {
          font-weight: 700;
        }
      `}</style>

      <EditorContent editor={editor} />

      <small
        style={{
          display: "flex",
          justifyContent: "flex-end",
          fontSize: "13px",
          color: remaining <= 0 ? "red" : remaining < 50 ? "orange" : "gray",
          marginTop: "4px",
        }}
      >
        {remaining} characters left
      </small>
    </>
  );
};

export default RichTextEditor;
